<template>
  <div class="plan-service-table">
    <div
      class="plan-service-table__head"
      v-bind:class="{ progress: isShowPlanProgress }">
      <div class="plan-service-table__cell">Услуги и товары</div>
      <div v-if="isShowPlanProgress" class="plan-service-table__cell">Оказано</div>
      <div class="plan-service-table__cell">Зубы</div>
      <div class="plan-service-table__cell">Кол-во</div>
      <div class="plan-service-table__cell">Цена</div>
      <div class="plan-service-table__cell discount">
        Скидка
        <switch-discount-type
          v-bind:value="initialDiscountType"
          v-on:input="emitDiscountType"
        />
      </div>
      <div class="plan-service-table__cell">Итого</div>
      <div class="plan-service-table__cell delete" />
    </div>
    <div v-if="hasStages" class="plan-service-table__body">
      <patient-plan-stage
        v-for="(stage, index) in stages"
        v-bind:is-show-total="isShowTotal"
        v-bind:key="`stage-${index}`"
        v-bind:aggregate-total="aggregateTotalByStages[index]"
        v-bind:discount-type="initialDiscountType"
        v-bind:is-show-plan-progress="isShowPlanProgress"
        v-bind:stage-name="stage.name"
        v-bind:stage-services="stage.serviceList"
        v-bind:drag-candidate="dragCandidate"
        v-on:add-service="emitAddServiceToStage(index)"
        v-on:change-stage="emitChangeStage(index, $event)"
        v-on:change-total-discount="onStageTotalDiscountChanged(index, $event)"
        v-on:delete="emitDeleteStage(index)"
        v-on:input-name="onInputStageName(index, $event)"
        v-on:input-rows="onInputRows(index, $event)"
        v-on:drag-enter="onDragEnter"
        v-on:drag-start="onDragStartStage(stage, $event)"
        v-on:drag-end="onDragEnd"
        v-on:drop="onDropStage(stage, $event)"
        v-on:move-end="onMoveStageEnd(stage, $event)"
        v-on:input-drag-last="onInputDragLast"
      />
    </div>
    <template v-else>
      <plan-service-table-row
        v-for="({ row: item, isDrag }, index) in services"
        v-bind:key="item.id || item.key"
        v-bind:item="item"
        v-bind:discount-type="initialDiscountType"
        v-bind:is-show-progress="isShowPlanProgress"
        v-bind:is-drag="isDrag"
        v-on:change="emitChange(index, $event)"
        v-on:delete="emitDelete(index)"
        v-on:drag-enter="onDragEnter"
        v-on:drag-start="onDragStart"
        v-on:drag-end="onDragEnd"
        v-on:drop="onDrop"
      />
      <div
        class="plan-service-table__last"
        :class="{ 'plan-service-table__drag-selected-element': isDropLast }"
        v-on:drop="onDropLast"
        v-on:dragover="onDragOverLast"
        v-on:dragleave="onDragLeaveLast"
      />
    </template>
    <div v-if="isShowResult" class="plan-service-table__results">
      <p>
        Итого: <b>
          <the-rub
            v-bind:show-kop="$options.SHOW_KOP_AUTO"
            v-bind:kop="aggregateTotal.amount"
            currency="руб" />
        </b>
      </p>
      <p>
        Скидка:
        <input-total-discount
          v-bind:disabled="false"
          v-bind:initial-discount-type="initialDiscountType"
          v-bind:initial-discount="aggregateTotal.discountWithoutFreeServices"
          v-bind:max-discount="aggregateTotal.amountWithoutFreeServices"
          v-bind:value="aggregateTotal.discount"
          v-on:change="onTotalDiscountChanged"
        />
      </p>
      <p>
        С учетом скидки: <b>
          <the-rub
            v-bind:show-kop="$options.SHOW_KOP_AUTO"
            v-bind:kop="aggregateTotal.final"
            currency="руб"
          />
        </b>
      </p>
    </div>
  </div>
</template>
<script>
import InputTotalDiscount from '@/components/InputTotalDiscount.vue';
import PatientPlanStage from '@/components/PatientPlanStage.vue';
import PlanServiceTableRow from '@/components/PlanServiceTableRow.vue';
import SwitchDiscountType from '@/components/SwitchDiscountType.vue';
import TheRub, { SHOW_KOP_AUTO } from '@/components/TheRub.vue';
import spreadDiscountMixin from '@/mixins/spreadDiscountMixin';

/**
 * @description Название события изменения услуги плана.
 * @type {string}
 */
const NAME_EVENT_CHANGE_ROW = 'change';

/**
 * @description Название события изменения этапа плана лечения.
 * @type {string}
 */
const NAME_EVENT_CHANGE_STAGE = 'change-stage';

/**
 * @description Название события удаления услуги плана.
 * @type {string}
 */
const NAME_EVENT_DELETED_ROW = 'delete';

/**
 * @description Название события удаления этапа плана лечения.
 * @type {string}
 */
const NAME_EVENT_DELETE_STAGE = 'delete-stage';

/**
 * @description Название события добавления услуги этапу.
 * @type {string}
 */
const NAME_EVENT_ADD_SERVICE_STAGE = 'add-stage-service';

/**
 * @description Название события удаления этапа плана лечения.
 * @type {string}
 */
const NAME_EVENT_INPUT_DISCOUNT_TYPE = 'input-discount-type';

/**
 * @description Начальное значение суммарных итогов.
 * @type {Object}
 */
const INITIAL_AGGREGATE_TOTAL = {
  amount: 0,
  amountWithoutFreeServices: 0,
  discount: 0,
  discountWithoutFreeServices: 0,
  final: 0,
};

/**
 * @description Таблица услуг.
 * @type {any}
 */
export default {
  SHOW_KOP_AUTO,
  components: {
    PatientPlanStage,
    PlanServiceTableRow,
    TheRub,
    SwitchDiscountType,
    InputTotalDiscount,
  },
  computed: {
    /**
     * @description Суммарные итоги по этапам.
     * @returns {Array}
     */
    aggregateTotalByStages() {
      return this.stages.map((stage) => this.calcAggregateTotal(stage.serviceList));
    },
    /**
     * @description Суммарные итоги.
     * @returns {Object}
     */
    aggregateTotal() {
      if (this.hasStages) {
        return this.aggregateTotalByStages.reduce(
          (total, stage) => ({
            amount: total.amount + stage.amount,
            amountWithoutFreeServices: total.amountWithoutFreeServices
              + stage.amountWithoutFreeServices,
            discount: total.discount + stage.discount,
            discountWithoutFreeServices: total.discountWithoutFreeServices
              + stage.discountWithoutFreeServices,
            final: total.final + stage.final,
          }),
          INITIAL_AGGREGATE_TOTAL,
        );
      }
      return this.calcAggregateTotal(this.rows);
    },
    /**
     * @description Показывать итоги.
     * @returns {boolean}
     */
    isShowResult() {
      if (this.hasStages) {
        return this.stages.some((candidate) => candidate.serviceList.length > 0);
      }
      return this.rows.length > 0;
    },
    /**
     * @description Может менять общую скидку.
     * @type {boolean}
     */
    canChangeTotalDiscount() {
      return true;
    },
    /**
     * @description Показывать результаты с итогами под каждым этапом.
     * @returns {boolean}
     */
    isShowTotal() {
      return this.stages.filter((candidate) => candidate.serviceList.length > 0).length > 1;
    },
    services() {
      return this.rows.map((row) => ({
        row,
        isDrag: this.dragCandidate === row && row !== this.dragTarget,
      }));
    },
  },
  methods: {
    calcAggregateTotal(services) {
      return services.reduce(
        (total, service) => ({
          amount: total.amount + service.amount.toKop(),
          amountWithoutFreeServices: total.amountWithoutFreeServices
            + (service.byVHIPolicy ? 0 : service.amount.toKop()),
          discount: total.discount + service.discountAmount.toKop(),
          discountWithoutFreeServices: total.discountWithoutFreeServices
              + (service.byVHIPolicy ? 0 : service.discountAmount.toKop()),
          final: total.final + service.finalPrice.toKop(),
        }),
        INITIAL_AGGREGATE_TOTAL,
      );
    },
    /**
     * @description Распределить скидку между позициями.
     * @param {number} value
     * @returns {void}
     */
    spreadDiscountBetweenPositions(value) {
      const rows = this.rows.filter((candidate) => !candidate.byVHIPolicy);
      if (rows.length > 0) {
        const discounts = this.spreadDiscount(
          this.initialDiscountType,
          value,
          rows,
          this.aggregateTotal.amountWithoutFreeServices,
        );
        rows.forEach((candidate, index) => {
          const rowIndex = this.rows.indexOf(candidate);
          const discount = candidate.discount.setValue(discounts[index]);
          this.emitChange(rowIndex, candidate.setDiscount(discount));
        });
      }
    },
    /**
     * @description Распределить скидку между позициями этапов.
     * @param {number} value
     * @returns {void}
     */
    spreadDiscountBetweenStages(value) {
      const payablePositions = this
        .stages
        .flatMap(
          (stage, stageIndex) => stage
            .serviceList
            .flatMap((candidate, candidateIndex) => (candidate.byVHIPolicy
              ? []
              : [{ stageIndex, candidate, candidateIndex }]
            )),
        );
      const rows = payablePositions.map(({ candidate }) => candidate);
      if (rows.length > 0) {
        const discounts = this.spreadDiscount(
          this.initialDiscountType,
          value,
          rows,
          this.aggregateTotal.amountWithoutFreeServices,
        );
        const positionsGroupByStage = payablePositions.reduce(
          (
            acc,
            { stageIndex, candidate, candidateIndex },
            index,
          ) => {
            const discount = candidate.discount.setValue(discounts[index]);
            const newPosition = candidate.setDiscount(discount);
            (acc[stageIndex] = acc[stageIndex] || {})[candidateIndex] = newPosition;
            return acc;
          },
          {},
        );
        Object.keys(positionsGroupByStage).forEach((stageIndex) => {
          const changes = positionsGroupByStage[stageIndex];
          const stage = this.stages[stageIndex];
          const serviceList = stage.serviceList.map(
            (candidate, candidateIndex) => changes[candidateIndex] || candidate,
          );
          this.emitChangeStage(stageIndex, stage.setServiceList(serviceList));
        });
      }
    },
    /**
     * @description Обработать изменение общей скидки.
     * @param {number} value
     * @returns {void}
     */
    onTotalDiscountChanged(value) {
      if (this.hasStages) {
        this.spreadDiscountBetweenStages(value);
      } else {
        this.spreadDiscountBetweenPositions(value);
      }
    },
    /**
     * @description Обработать изменение общей скидки этапа.
     * @param {number} value
     * @returns {void}
     */
    onStageTotalDiscountChanged(index, value) {
      const stage = this.stages[index];
      const rows = stage.serviceList.filter((candidate) => !candidate.byVHIPolicy);
      if (rows.length > 0) {
        const discounts = this.spreadDiscount(
          this.initialDiscountType,
          value,
          rows,
          this.aggregateTotalByStages[index].amountWithoutFreeServices,
        );
        const changes = rows.reduce((acc, candidate, candidateIndex) => {
          const rowIndex = stage.serviceList.indexOf(candidate);
          const discount = candidate.discount.setValue(discounts[candidateIndex]);
          acc[rowIndex] = candidate.setDiscount(discount);
          return acc;
        }, {});
        const serviceList = stage.serviceList.map(
          (candidate, candidateIndex) => changes[candidateIndex] || candidate,
        );
        this.emitChangeStage(index, stage.setServiceList(serviceList));
      }
    },
    /**
     * @description Обработать изменение услуг этапа.
     * @param {number}        index
     * @param {PlanService[]} rows
     * @returns {void}
     */
    onInputRows(index, rows) {
      const stage = this.stages[index];
      this.emitChangeStage(index, stage.setServiceList(rows));
    },
    /**
     * @description Обработать изменение названия этапа.
     * @param {number} index
     * @param {string} name
     * @returns {void}
     */
    onInputStageName(index, name) {
      const stage = this.stages[index];
      this.emitChangeStage(index, stage.setName(name));
    },
    /**
     * @description Отправить событие добавления услуги к этапу
     * @param index
     */
    emitAddServiceToStage(index) {
      this.$emit(NAME_EVENT_ADD_SERVICE_STAGE, index);
    },
    /**
     * @description Отправить событие изменения услуги.
     * @param {number} index Номер строки.
     * @param {any}    value Строка.
     */
    emitChange(index, value) {
      this.$emit(NAME_EVENT_CHANGE_ROW, index, value);
    },
    /**
     * @description Отправить событие удаления услугу.
     * @param {number} index Номер строки.
     * @returns {void}
     */
    emitDelete(index) {
      this.$emit(NAME_EVENT_DELETED_ROW, index);
    },
    /**
     * @description Отправить событие изменения этапа
     * @param index
     * @param value
     */
    emitChangeStage(index, value) {
      this.$emit(NAME_EVENT_CHANGE_STAGE, index, value);
    },
    /**
     * @description Отправить событие удаления этапа
     * @param index
     */
    emitDeleteStage(index) {
      this.$emit(NAME_EVENT_DELETE_STAGE, index);
    },
    /**
     * @description Отправить событие изменения типа скидки.
     * @param {boolean} value
     * @returns {void}
     */
    emitDiscountType(value) {
      this.$emit(NAME_EVENT_INPUT_DISCOUNT_TYPE, value);
    },
    onDragStartStage(stage, item) {
      this.fromStage = stage;
      this.onDragStart(item);
    },
    onDragEnter(item) {
      if (this.isDropLast !== true) {
        this.dragCandidate = item;
      }
    },
    onDragStart(item) {
      this.dragTarget = item;
    },
    onDragEnd() {
      this.dragCandidate = null;
      this.dragTarget = null;
      this.fromStage = null;
      this.isDropLast = false;
    },
    onDrop(item) {
      if (this.dragTarget === item) {
        this.onDragEnd();
        return;
      }
      this.$emit('move-row', this.dragTarget, item);
      this.onDragEnd();
    },
    onDropStage(toStage, item) {
      if (this.dragTarget === item) {
        this.onDragEnd();
        return;
      }
      this.$emit('move-row-between-stages', this.fromStage, toStage, this.dragTarget, item);
      this.onDragEnd();
    },
    onMoveStageEnd(toStage) {
      this.$emit('move-row-between-stages-last', this.fromStage, toStage, this.dragTarget);
      this.onDragEnd();
    },
    onDropLast() {
      this.$emit('move-row-last', this.dragTarget);
      this.onDragEnd();
    },
    onDragOverLast(e) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      this.isDropLast = true;
      this.dragCandidate = null;
    },
    onDragLeaveLast() {
      this.isDropLast = false;
    },
    onInputDragLast(value) {
      if (value) {
        this.dragCandidate = null;
      }
    },
  },
  data() {
    return {
      dragCandidate: null,
      dragTarget: null,
      fromStage: null,
      isDropLast: false,
    };
  },
  name: 'PlanServiceTable',
  mixins: [spreadDiscountMixin],
  props: {
    /**
     * @description Показывать прогресс.
     * @type {boolean}
     */
    isShowPlanProgress: { required: true, type: Boolean },
    /**
     * @description Тип скидки.
     * @type {boolean}
     */
    initialDiscountType: { required: true, type: String },
    /**
     * @description Строки таблицы.
     * @type {any[]}
     */
    rows: { required: true, type: Array },
    /**
     * @description Этапы
     * @type {any[]}
     */
    stages: { default: () => [], type: Array },
    /**
     * @description Имеются ли этапы
     * @type {boolean}
     */
    hasStages: { default: () => false, type: Boolean },
  },
};
</script>
<style lang="scss">
.plan-service-table {
  counter-reset: counterServices;
  display: flex;
  flex-direction: column;
  margin: 40px 0 0 0;
  &__head,
  &__row {
    display: grid;
    grid-template-columns: minmax(200px, 1fr) 78px 75px 95px 145px 115px 43px;
    align-items: center;
    font-size: 14px;
    line-height: 17px;
    &.progress {
      grid-template-columns: minmax(200px, 1fr) 80px 78px 75px 115px 125px 115px 43px;
    }
  }
  &__head {
    font-weight: 700;
    border-bottom: 2px solid #0489e0;
  }
  &__body {
    border-bottom: 2px solid #0489e0;
    padding: 0 0 12px 0;
  }
  &__row {
    &.violation {
      border-color: var(--warning-color);
      .plan-service-table__cell__name {
        color: var(--warning-color) !important;
      }
    }
    &.new {
      background: #eaeff3;
    }
    border-bottom: 1px solid #eaeff3;
    &:hover {
      background: #eaeff3;
    }
  }
  &__unit {
    padding: 12px 8px;
  }
  &__results {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 28px 0 0 auto;
    width: 310px;
    p {
      font-weight: 700;
      font-size: 16px;
      line-height: 19px;
      color: #707683;
      margin-bottom: 0 !important;
      b {
        color: #323c47;
      }
    }
  }
  &__cell {
    padding: 12px 8px;
    &:focus {
      padding: 5px;
      outline-color: var(--action-color);
    }
    .theme--light.v-icon:focus:after {
      opacity: 0;
    }
    &.number:after {
      counter-increment: counterServices;
      content: counter(counterServices);
    }
    &__color {
      width: 15px;
      height: 15px;
      border-radius: 50%;
    }
    &__name {
      text-align: left;
      padding: 12px 8px !important;
    }
    &.completed {
      background-image: url("@/assets/img/checked/checked-white-top.svg");
      background-color: var(--action-color);
      border-color: var(--action-color);
      background-position: center center;
      background-repeat: no-repeat;
      background-size: 70%;
      border: 1px solid var(--unfocus-color);
      border-radius: 4px;
      height: 1.5rem;
      width: 1.5rem;
      cursor: default;
      padding: 0;
      justify-self: center;
    }
    &.progress {
      justify-self: center;
    }
    &.tooth {
      background-image: url("@/assets/img/tooth.svg");
      width: 100%;
      height: 30px;
      background-size: contain;
      margin-left: 12px;
      background-repeat: no-repeat;
      cursor: pointer;
    }
    &.vhi {
      justify-self: center;
      & > span::before {
        margin: 0 !important;
      }
    }
    &.discount {
      display: flex;
      align-items: center;
      margin-right: 35px;
      gap: 5px;
      & > input {
        background-color: white;
        border: 1px solid #0489e0;
        border-radius: 4px;
        width: 100%;
        &:focus {
          color: #212529;
          outline: 0;
        }
        &:disabled {
          color: #212529;
          cursor: default;
        }
      }
    }
    &.edit {
      background-color: white;
      border: 1px solid #0489e0 !important;
      border-radius: 4px;
      width: 100%;
      padding: 5px 8px;
      &:focus {
        color: #212529;
        border: 1px solid #0489e0 !important;
        outline: 0;
      }
    }
    &__name,
    &__teeth {
      cursor: pointer;
      margin: 0 !important;
      font-size: 14px;
      line-height: 17px;
      .v-input__control > .v-input__slot {
        cursor: pointer;
        &::before,
        &::after {
          border: none !important;
        }
        .v-select__slot > input {
          padding: 0 !important;
        }
      }
      .v-autocomplete__content {
        top: 45px !important;
      }
      & .v-list-item__title {
        white-space: pre-line;
      }
    }
  }
  &__icon {
    &-copy {
      background-image: url("@/assets/img/copy-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 14px;
      height: 14px;
      padding: 0;
      margin: 0;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/copy-blue.svg");
      }
    }
    &-edit {
      background-image: url("@/assets/img/pen-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 14px;
      height: 14px;
      padding: 0;
      margin: 0;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/pen-blue.svg");
      }
    }
    &-delete {
      display: block;
      background-image: url("@/assets/img/exit-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 12px;
      height: 12px;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/exit-blue.svg");
      }
    }
  }
  &__last {
    width: 100%;
    height: 10px;
    &.plan-service-table__drag-selected-element {
      height: 50px;
    }
  }
  &__drag-selected-element{
    border-image: linear-gradient(#0489e0 0%, #ffffff 2%) 1;
    border-top: 15px;
    border-style: solid;
  }
}
@media screen and (max-width: 1400px) {
  .plan-service-table {
    overflow-x: scroll;
    &__row,
    &__head {
      width: 100%;
      min-width: 1200px;
    }
  }
}
@media screen and (max-width: 520px) {
  .plan-service-table {
    &__results {
      margin-left: 0;
    }
  }
}
@media print {
  .plan-service-table {
    counter-reset: counterServices;
    display: flex;
    flex-direction: column;
    margin: 40px 0 0 0;
    &__head,
    &__row {
      align-items: center;
      display: grid;
      font-size: 14px;
      grid-template-columns: 200px 108px 90px 75px 78px 123px 50px 123px;
      line-height: 17px;
      &.progress {
        grid-template-columns: 80px 200px 108px 90px 75px 78px 123px 50px 123px;
      }
    }
  }
  .delete, .plan-service-table__icon-delete {
    display: none;
  }
}
</style>
